import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '@environments/environment';

import { BaseService } from '@aifs-shared/common/base-service';
import { BaseResponse } from '@aifs-shared/common/base-response';

import { Observable, Subject } from 'rxjs';
import { ResponseData } from '@aifs-shared/common/response-data';

@Injectable()
export class PoliceCheckService extends BaseService {

    public constructor(
        private http: HttpClient
    ) {
        super();
    }
    
    registerWithKYP(applicationId: number): Observable<boolean> {
        const subject = new Subject<any>()

        this.http
            .post<BaseResponse>(environment.ServiceUrl_DBS_Register(applicationId), {})
            .subscribe({
                next: (data: BaseResponse) => {
                    subject.next(true);
                },
                error: (error:any) => {
                    if (error.status === 409) {
                        alert('Something went wrong - applicant already registered! Visit knowyourpeople.co.uk to learn more about the application status.')
                    } 
                    else if (error.status === 400) {
                        alert('Something went wrong! Visit knowyourpeople.co.uk to learn more about the application status.')
                    } else {
                        // Handle other errors
                        subject.error(error);
                    }
                }
            });

        return subject
    }

    overrideOnlineCheckWithPaperCertificate(applicationId: number): Observable<boolean> {
        const subject = new Subject<any>()

        this.http
            .post<BaseResponse>(environment.ServiceUrl_DBS_OverrideOnlineCheckWithPaperCertificate(applicationId), {})
            .subscribe({
                next: (data: BaseResponse) => {
                    subject.next(true);
                },
                error: (error:any) => {
                    alert('Something went wrong! Could not switch to paper check.')
                }
            });

        return subject
    }

    sendReminderEmail(applicationId: number): Observable<boolean> {
        const subject = new Subject<any>()

        this.http
            .post<BaseResponse>(environment.ServiceUrl_DBS_SendReminderEmail(applicationId), {})
            .subscribe({
                next: (data: BaseResponse) => {
                    subject.next(true);
                },
                error: (error:any) => {
                    alert('Something went wrong please wait and try again later or visit knowyourpeople.co.uk to learn more about the application status.')
                }
            });

        return subject
    }

    setPoliceCheckResult(applicationId: number, resultCode: string): Observable<boolean> {
        const subject = new Subject<any>()

        this.http
            .post<BaseResponse>(environment.ServiceUrl_PaperPoliceCheck_SetResult(applicationId, resultCode), {})
            .subscribe({
                next: (data: BaseResponse) => {
                    subject.next(true);
                },
                error: (error:any) => {
                    subject.error(error);
                }
            });

        return subject
    }

    public getDBSStatus(applicationId: number): Observable<KYPApplicationDetailsResponse> {
        const s = new Subject<KYPApplicationDetailsResponse>();

        this.http
            .get(environment.ServiceUrl_Applicant_DBSDetails(applicationId))
            .subscribe({
                next: (data: any) => {
                    if (data.result) {
                        const response = this.getResultData<KYPApplicationDetailsResponse>(data);
                        s.next(response);
                    }
                },
                error: (error: any) => {
                    s.error(error);
                }
            });

        return s;
    }

    public getPaperPoliceCheckDetails(applicationId: number): Observable<PaperPoliceCheckStatusDetails> {
        const s = new Subject<PaperPoliceCheckStatusDetails>();

        this.http
            .get(environment.ServiceUrl_PaperPoliceCheck_GetDetails(applicationId))
            .subscribe({
                next: (data: any) => {
                    if (data.result) {
                        const response = this.getResultData<PaperPoliceCheckStatusDetails>(data);
                        s.next(response);
                    }
                },
                error: (error: any) => {
                    s.error(error);
                }
            });

        return s;
    }

    public isApplicantInGBGCountry(applicationId: number): Observable<AllowedForKYPRegistration> {
        let s = new Subject<AllowedForKYPRegistration>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_DBS_GetDBSProspectDetails(applicationId))
            .subscribe(
                data => {
                    const response = data.result as AllowedForKYPRegistration;

                    s.next(response);
                },
                error => {
                    s.error(error);
                });

        return s;
    }

    public getOnlinePoliceCheckStatusDescription(status: KYPApplicationStatus, actionBy: KYPAwaitingActionBy): string {
        switch (status) {
            case KYPApplicationStatus.InProgress:
                return `In Progress`;
            case KYPApplicationStatus.AwaitingAction:
                return `Awaiting Action by (${KYPAwaitingActionBy[actionBy]})`;
            case KYPApplicationStatus.AwaitingDbsReference:
                return 'Awaiting DBS Reference';
            case KYPApplicationStatus.AwaitingVerification:
                return 'AwaitingVerification';
            case KYPApplicationStatus.AwaitingCountersign:
                return 'Awaiting Countersign';
            case KYPApplicationStatus.ToBeCompleted:
                return 'To Be Completed';
            case KYPApplicationStatus.AwaitingPayment:
                return 'Awaiting Payment';
            case KYPApplicationStatus.Completed:
                return `Completed`;
            case KYPApplicationStatus.Withdrawn:
                return 'Withdrawn';
            case KYPApplicationStatus.NotRegistered:
                return 'Not Registered';
            case KYPApplicationStatus.Deferred:
                return 'Deferred'
            default:
                return '';
        }
    }

    public getOnlinePoliceCheckStatusHistory(applicationId: number): Observable<KYPApplicationDetailsHistory> {
        let s = new Subject<KYPApplicationDetailsHistory>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_DBS_ApplicationStatusHistory(applicationId))
            .subscribe(
                data => {
                    const response = data.result as KYPApplicationDetailsHistory;

                    s.next(response);
                },
                error => {
                    s.error(error);
                });

        return s;
    }

    getOrganisationType(organisationType: number): string {
        switch (organisationType) {
            case 1: return 'Online';
            case 2: return 'Post Office';
            case 3: return 'Paper';
        }
        return '';
    }

    getPaperCheckStatus(crbStatus: string): string {
        switch (crbStatus) {
            // case 'O':
            // case 'A':
            case 'P':
                return 'Police Certificate Received';
            default:
                return 'Police Certificate Missing';
        }
    }

    getPaperCheckResultText(crbResult: string): string {
        switch (crbResult) {
            case 'C': return 'Clear';
            case 'D': return 'Discrepancy';
            case 'P': return 'See Paper Disclosure'
        }
        return '';
    }
}

export interface AllowedForKYPRegistration {
    allowedForKYP: boolean;
    isRegistered: boolean;
    isPostOffice: boolean;
    isOverriddenByPaperCheck: boolean;
}

export class KYPApplicationDetailsHistory implements ResponseData {
    history?: KYPApplicationDetailsUpdate[]
}

export interface PaperPoliceCheckStatusDetails{
    isPaperCheck: boolean;
    paperPoliceCheckReceived: boolean;
    paperPoliceCheckStatus: string;
    paperPoliceCheckResult: string;
}

export class KYPApplicationDetailsResponse implements ResponseData {
    details!: KYPApplicationDetails;
}

export interface KYPApplicationDetails {
    applicationStatus: KYPApplicationStatus;
    awaitingActionBy: KYPAwaitingActionBy;
    applicationResult: KYPApplicationResult;
}

export interface KYPApplicationDetailsUpdate {
    applicationStatus: KYPApplicationStatus;
    applicationResult: KYPApplicationResult;
    dateUpdated: Date;
}

export interface DBSStatusUpdate {
    id: number;
    status: number;
    statusDescription: string;
    dateAdded: Date;
}

export interface DBSComment {
    commentDate: string;
    commentBy: string;
    comment: string;
    status: number;
    statusDescription: string;
    organisationType: number;
    organisationDescription: string;
    clientReference: string;
    organisationId: number;
}


export enum KYPApplicationStatus
{
    Unknown,
    ToBeCompleted,
    InProgress,
    AwaitingAction,
    Completed,
    Withdrawn,
    AwaitingVerification,
    AwaitingCountersign,
    AwaitingPayment,
    AwaitingDbsReference,
    NotRegistered,
    Deferred
}

export enum KYPApplicationResult
{
    Unknown,
    Clear,
    Indeterminate,
    NoCertificate,
    Dispute,
    NotClear
}

export enum KYPAwaitingActionBy
{
    NA,
    Customer,
    KYP,
    Applicant
}